/**
 * Disclaimer catalogue — one place that owns every helper-tool disclaimer
 * blurb the app can display, keyed by CONTEXT.
 *
 * Post soft-launch unification (2026-02-15): every context now returns
 * the SAME single-sentence helper line. Longer per-surface prose was
 * removed because tradies were seeing "General advice only. General
 * advice only…" duplicates and legal-drift risk. Adding an actual copy
 * variant now requires a deliberate override here — intentional friction.
 *
 * The ruleset stamp is kept SEPARATE from the sentence so callers can
 * render it as a tight chip ("ATO ruleset 2026.1") rather than baking it
 * into a paragraph. That decision is enforced by having `composeDisclaimer`
 * append the stamp with a space so callers who want it inline still can,
 * but the recommended pattern is `<AtoDisclaimer>` rendering the two on
 * separate lines.
 */

import { activeRuleset } from "./rulesets";

/** All UI surfaces that need a disclaimer are registered here. Adding a new
 *  context requires adding an entry — this is intentional friction. */
export type DisclaimerContext =
  | "capture" // Review screen / editable fields
  | "tax_summary" // BAS / TPAR / quarterly health
  | "chat" // AI chat / deduction checker
  | "personal_tax" // Rentals / investments / deductions
  | "export" // CSV / PDF / share flows
  | "generic"; // Fallback catch-all

interface DisclaimerCopy {
  /** Short one-liner — used inline beside CTAs. */
  short: string;
  /** Full sentence — used in cards + first-time contexts. */
  full: string;
}

/**
 * Canonical helper-tool line — the single source of truth. Kept identical
 * to the string used in `pdf_footer.py::FOOTER_DISCLAIMER` and the
 * `PublicTrustFooter` on the marketing site so no matter which surface a
 * tradie is on, the wording is the same.
 */
export const HELPER_LINE =
  "Helper tool only — not a registered tax agent. Confirm figures with your accountant before lodgement.";

const UNIFIED: DisclaimerCopy = {
  short: HELPER_LINE,
  full: HELPER_LINE,
};

const CATALOGUE: Record<DisclaimerContext, DisclaimerCopy> = {
  capture: UNIFIED,
  tax_summary: UNIFIED,
  chat: UNIFIED,
  personal_tax: UNIFIED,
  export: UNIFIED,
  generic: UNIFIED,
};

/**
 * Ruleset chip text — deliberately short. Callers render this next to the
 * disclaimer as its own small element (see `AtoDisclaimer`), never merged
 * into the paragraph.
 */
export const rulesetStamp = (): string =>
  `ATO ruleset ${activeRuleset.version}`;

/** Look up the copy for a given surface. Falls back to `generic`. */
export const disclaimerFor = (context: DisclaimerContext): DisclaimerCopy =>
  CATALOGUE[context] ?? CATALOGUE.generic;

/**
 * Compose the disclaimer string (copy + ruleset stamp) as a plain string.
 * Handy for exports, PDFs, and copy-to-clipboard flows where the React
 * component isn't available.
 */
export const composeDisclaimer = (
  context: DisclaimerContext,
  variant: "short" | "full" = "full"
): string => {
  const copy = disclaimerFor(context)[variant];
  return `${copy} ${rulesetStamp()}`;
};

/**
 * Type guard — refuse to render tax-adjacent UI without a disclaimer. Used
 * inside <AtoDisclaimer />; also exported so component tests can assert
 * that every tax surface has one.
 */
export const isKnownDisclaimerContext = (
  v: string
): v is DisclaimerContext =>
  ["capture", "tax_summary", "chat", "personal_tax", "export", "generic"]
    .includes(v);
